import { api } from "./client";

/**
 * Connector status API — backed by the `connector_heartbeat` table via the
 * `/connectors` router. Each worker upserts a heartbeat every poll cycle, so
 * `status` reflects true liveness (worker up AND able to reach the DB), not
 * just the age of the connector's data table.
 *
 * Timestamps come back as Nashville-local naive ISO strings.
 */

export type ConnectorStatusValue = "online" | "degraded" | "offline";

/** One label/value line of the latest data pull. */
export type ConnectorSnapshotItem = { label: string; value: string };

export type ConnectorSnapshot = {
  /** Timestamp of the source row, Nashville-local ISO. */
  captured_at: string | null;
  items: ConnectorSnapshotItem[];
};

/** Health of one PLC behind a connector that polls several. */
export type ConnectorPart = {
  /** Port number, as a string. */
  key: string;
  label: string;
  /** The port this PLC answers on. */
  port: number | null;
  /** Newest row this PLC produced, or null when it has never written one. */
  last_data: ConnectorSnapshot | null;
  status: ConnectorStatusValue;
  /** Answered on the worker's most recent cycle. */
  reading: boolean;
  seconds_since_ok: number | null;
  last_ok_at: string | null;
  error: string | null;
};

export type ConnectorStatus = {
  key: string;
  name: string;
  worker: string;
  table: string;
  /** Transport the worker uses to reach the PLC. */
  protocol: string;
  /** What this connector covers, in prose. */
  description: string;
  /** Signals this connector supplies. */
  provides: string[];
  status: ConnectorStatusValue;
  /** Configured poll interval in seconds. */
  poll_seconds: number | null;
  /** "worker" = reported by the running worker; "expected" = configured default. */
  poll_seconds_source: "worker" | "expected";
  /** Last completed poll cycle (heartbeat), Nashville-local ISO, or null. */
  last_beat_at: string | null;
  seconds_since_beat: number | null;
  /** Last time a real data row was written (lags the beat for idle blenders /
   *  between-roll rollers), Nashville-local ISO, or null. */
  last_data_at: string | null;
  seconds_since_data: number | null;
  /** Values the worker reported for its last completed cycle, or null. */
  detail: Record<string, unknown> | null;
  /**
   * Per-PLC health, for a worker polling several in one cycle (the blenders
   * connector reads five). Empty for single-PLC connectors.
   */
  parts: ConnectorPart[];
  /** Newest row in the connector's data table, or null when there is none. */
  snapshot: ConnectorSnapshot | null;
};

/**
 * Connectors withheld from the UI entirely.
 *
 * `rollers` (Lam 1 Roll Lengths) is retired — roller-worker was stopped when
 * Line 1's PLC was replaced, and `lam1` now covers that machine. Its
 * `roll_lengths` history is kept but no longer grows, so the connector would
 * sit permanently offline and hold the sidebar's health badge red over a
 * machine nobody is waiting on.
 *
 * The current backend has dropped `rollers` from /connectors/status, so this
 * only still matters against a backend older than that. Kept as the shim for
 * exactly that case, and filtered here rather than at each call site so the
 * page, the nav badge and anything added later cannot disagree about what
 * exists.
 */
export const HIDDEN_CONNECTOR_KEYS: readonly string[] = ["rollers"];

/** Live status for every connector shown in the UI (fixed order). */
export function getConnectorStatus(signal?: AbortSignal) {
  return api
    .get<ConnectorStatus[]>("/connectors/status", { signal })
    .then((r) =>
      (r.data ?? []).filter((c) => !HIDDEN_CONNECTOR_KEYS.includes(c.key))
    );
}
